/**
 * A GST tax invoice, laid out the way Indian marketplace invoices are read:
 * a branded header that says TAX INVOICE, the order and invoice references,
 * Sold by / Bill to / Ship to blocks, a numbered tax table with the CGST+SGST
 * or IGST split per line, totals, the amount in words and a signatory block.
 * Prints to a single A4 page.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileText, Printer } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, PageHeader, Spinner } from '@terra/ui';
import { STATE_NAME_BY_CODE, amountInWords, gstStateCode } from '@terra/utils';

import { api } from '../lib/api';

const inr = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const stateName = (code: string | null | undefined) =>
  code ? (STATE_NAME_BY_CODE[gstStateCode(code) ?? ''] ?? code) : null;

const fmtDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="p-4">
      <p className="mb-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#a8161b]">
        {title}
      </p>
      <div className="space-y-0.5 text-[12.5px] leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

export function InvoicePage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vendor-tax-invoice', orderId],
    queryFn: () => api.orders.getTaxInvoice(orderId!),
    enabled: !!orderId,
  });
  // For invoices issued before the order reference was frozen onto them.
  const { data: subOrder } = useQuery({
    queryKey: ['vendor-order-detail', orderId],
    queryFn: () => api.orders.getVendorSubOrder(orderId!),
    enabled: !!orderId && !!invoice && !invoice.buyerSnapshot.orderNumber,
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Invoice not available"
          description={
            (error instanceof Error ? error.message : null) ??
            'The invoice is issued when the shipment is created. Create the shipment first.'
          }
        />
        <Button onClick={() => navigate(`/orders/${orderId}`)}>← Back to order</Button>
      </div>
    );
  }

  const seller = invoice.sellerSnapshot;
  const buyer = invoice.buyerSnapshot;
  const addr = buyer.shippingAddress ?? {};
  const orderNumber =
    buyer.orderNumber ??
    (subOrder as { orderGroup?: { orderNumber?: string } } | undefined)?.orderGroup?.orderNumber;
  const orderedAt =
    buyer.orderedAt ?? (subOrder as { createdAt?: string } | undefined)?.createdAt ?? null;
  const placeOfSupply = stateName(buyer.placeOfSupplyStateCode);
  const paidByBuyer = invoice.grandTotalPaise - (invoice.platformDiscountPaise ?? 0);
  const inter = invoice.isInterState;

  const buyerLines = (
    <>
      <p className="font-bold text-slate-900">{buyer.name ?? addr.contactName ?? 'Customer'}</p>
      {addr.line1 && <p>{addr.line1}</p>}
      {addr.line2 && <p>{addr.line2}</p>}
      <p>{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
      {(addr.contactPhone ?? buyer.phone) && <p>Phone: {addr.contactPhone ?? buyer.phone}</p>}
    </>
  );

  return (
    <div className="space-y-4">
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body * { visibility: hidden; }
          #tax-invoice, #tax-invoice * { visibility: visible; }
          #tax-invoice { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; }
          #tax-invoice { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Button variant="outline" onClick={() => navigate(`/orders/${orderId}`)}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to order
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="mr-1.5 h-4 w-4" /> Print / save as PDF
        </Button>
      </div>

      <div
        id="tax-invoice"
        className="mx-auto max-w-4xl overflow-hidden rounded-xl border-2 border-slate-800 bg-white text-slate-800 shadow-lg"
      >
        {/* Header band */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-gradient-to-r from-[#a8161b] to-[#c2410c] px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/image.png"
              alt="Terra Shoppy"
              className="h-12 w-auto rounded-md bg-white p-1"
            />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                Sold via Terra Shoppy
              </p>
              <p className="text-lg font-black leading-tight">{seller.legalName}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="flex items-center justify-end gap-2 text-2xl font-black tracking-wide">
              <FileText className="h-6 w-6" /> TAX INVOICE
            </p>
            <p className="text-[11px] font-semibold text-white/80">Original for recipient</p>
          </div>
        </div>

        {/* References */}
        <div className="grid grid-cols-2 gap-px border-b-2 border-slate-800 bg-slate-200 text-[12px] sm:grid-cols-4">
          {[
            ['Invoice no.', invoice.invoiceNumber, true],
            ['Invoice date', fmtDate(invoice.issuedAt), false],
            ['Order no.', orderNumber ?? '—', true],
            ['Order date', fmtDate(orderedAt), false],
          ].map(([k, v, mono]) => (
            <div key={String(k)} className="bg-amber-50/70 px-4 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k}</p>
              <p className={`font-extrabold text-slate-900 ${mono ? 'font-mono' : ''}`}>{v}</p>
            </div>
          ))}
        </div>

        {/* Parties */}
        <div className="grid divide-y divide-slate-200 border-b-2 border-slate-800 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <Block title="Sold by">
            <p className="font-bold text-slate-900">{seller.legalName}</p>
            {seller.address && <p>{seller.address}</p>}
            <p>
              {[seller.city, stateName(seller.stateCode) ?? seller.state, seller.pincode]
                .filter(Boolean)
                .join(', ')}
            </p>
            <p>
              <span className="font-semibold">GSTIN:</span> {seller.gstin ?? 'Unregistered'}
            </p>
            {seller.pan && (
              <p>
                <span className="font-semibold">PAN:</span> {seller.pan}
              </p>
            )}
          </Block>
          <Block title="Billing address">{buyerLines}</Block>
          <Block title="Shipping address">
            {buyerLines}
            {placeOfSupply && (
              <p className="pt-1">
                <span className="font-semibold">Place of supply:</span> {placeOfSupply} (
                {gstStateCode(buyer.placeOfSupplyStateCode) ?? buyer.placeOfSupplyStateCode})
              </p>
            )}
          </Block>
        </div>

        {/* Items */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-[11.5px]">
            <thead>
              <tr className="bg-slate-800 text-[10px] uppercase tracking-wider text-white">
                <th className="px-3 py-2.5">#</th>
                <th className="px-3 py-2.5">Product</th>
                <th className="px-3 py-2.5">HSN</th>
                <th className="px-3 py-2.5 text-right">Qty</th>
                <th className="px-3 py-2.5 text-right">Rate</th>
                <th className="px-3 py-2.5 text-right">Taxable</th>
                {inter ? (
                  <th className="px-3 py-2.5 text-right">IGST</th>
                ) : (
                  <>
                    <th className="px-3 py-2.5 text-right">CGST</th>
                    <th className="px-3 py-2.5 text-right">SGST</th>
                  </>
                )}
                <th className="px-3 py-2.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineSnapshot.map((li, i) => (
                <tr
                  key={`${li.sku}-${i}`}
                  className={`border-b border-slate-200 align-top ${i % 2 ? 'bg-slate-50/70' : ''}`}
                >
                  <td className="px-3 py-2.5 font-semibold text-slate-500">{i + 1}</td>
                  <td className="max-w-[260px] px-3 py-2.5">
                    <p className="font-semibold text-slate-900">{li.description}</p>
                    <p className="text-[10.5px] text-slate-500">SKU {li.sku}</p>
                  </td>
                  <td className="px-3 py-2.5 font-mono">{li.hsnSac}</td>
                  <td className="px-3 py-2.5 text-right">{li.qty}</td>
                  <td className="px-3 py-2.5 text-right">{inr(li.unitPricePaise)}</td>
                  <td className="px-3 py-2.5 text-right">{inr(li.taxableValuePaise)}</td>
                  {inter ? (
                    <td className="px-3 py-2.5 text-right">
                      {inr(li.igstPaise)}
                      <span className="block text-[10px] text-slate-500">@{li.gstRate}%</span>
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-2.5 text-right">
                        {inr(li.cgstPaise)}
                        <span className="block text-[10px] text-slate-500">@{li.gstRate / 2}%</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {inr(li.sgstPaise)}
                        <span className="block text-[10px] text-slate-500">@{li.gstRate / 2}%</span>
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2.5 text-right font-bold text-slate-900">
                    {inr(li.lineTotalPaise)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals + words */}
        <div className="grid border-t-2 border-slate-800 sm:grid-cols-[1fr_320px]">
          <div className="space-y-3 p-4 text-[12px]">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#a8161b]">
                Amount in words
              </p>
              <p className="font-semibold text-slate-900">
                {amountInWords(invoice.grandTotalPaise)}
              </p>
            </div>
            <p className="text-[11px] text-slate-500">
              Tax is {inter ? 'IGST (inter-state supply)' : 'CGST + SGST (intra-state supply)'}.
              Whether tax is payable under reverse charge: No.
            </p>
          </div>
          <div className="border-t border-slate-200 bg-slate-50 p-4 text-[12.5px] sm:border-l sm:border-t-0">
            <div className="flex justify-between py-0.5">
              <span>Taxable value</span>
              <span>{inr(invoice.taxableValuePaise)}</span>
            </div>
            {inter ? (
              <div className="flex justify-between py-0.5">
                <span>IGST</span>
                <span>{inr(invoice.igstPaise)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between py-0.5">
                  <span>CGST</span>
                  <span>{inr(invoice.cgstPaise)}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span>SGST</span>
                  <span>{inr(invoice.sgstPaise)}</span>
                </div>
              </>
            )}
            <div className="mt-2 flex items-center justify-between rounded-lg bg-[#a8161b] px-3 py-2 text-white">
              <span className="font-bold">Invoice total</span>
              <span className="text-lg font-black">{inr(invoice.grandTotalPaise)}</span>
            </div>
            {(invoice.platformDiscountPaise ?? 0) > 0 && (
              <>
                <div className="mt-2 flex justify-between text-slate-500">
                  <span>Discount (funded by Terra Shoppy)</span>
                  <span>−{inr(invoice.platformDiscountPaise ?? 0)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Paid by customer</span>
                  <span>{inr(paidByBuyer)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Signatory */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-t-2 border-slate-800 px-4 py-4 text-[11.5px]">
          <div className="max-w-md text-slate-500">
            <p className="font-semibold text-slate-700">Declaration</p>
            <p>
              We declare that this invoice shows the actual price of the goods described and that
              all particulars are true and correct. This is a computer-generated invoice.
            </p>
          </div>
          <div className="text-right">
            <p className="font-bold text-slate-900">For {seller.legalName}</p>
            <div className="my-3 h-8" />
            <p className="border-t border-slate-400 pt-1 text-slate-600">Authorised signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}
